import { OnBoardingPersonalInformationProps } from '@/types/props/components/onboarding';
import SpiritoryLabel from '@/components/common/Forms/SpiritoryLabel';
import PhoneDropdown from '@/components/common/phoneDropdown';
import SpiritorySelect from '@/components/common/Forms/SpiritorySelect';
import { PrimaryButton } from '@/components/common/PrimaryButton';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import useTypedTranslation from '@/hooks/useTypedTranslation';
import { OnboardingEnum } from '@/types/translations/OnboardingEnum';
import { countriesWithFlagsAndPrefix } from '@/constants/countries-with-flag';
import { useCountries } from '@/hooks/useCountries';
import { CommonEnum } from '@/types/translations/CommonEnum';
import { getValidLocale } from '@/utilites/language/language.utils';
import SpiritoryInput from '@/components/common/Forms/SpiritoryInput';
export default function OnBoardingPersonalInformation({
  user,
  currentPhonePrefix,
  setCurrentPhonePrefix,
  handleUserChange,
  submitUser,
  loading,
  handleDobChange,
  handleStep,
  loadingSubmitUser,
  isBuyerFlow,
  handleUserAddressChange,
}: OnBoardingPersonalInformationProps) {
  const { translate } = useTypedTranslation<OnboardingEnum>('onboarding');
  const { translate: translateCommon } = useTypedTranslation<CommonEnum>('common');
  const countries = useCountries();
  const router = useRouter();
  const [submitDisabled, setSubmitDisabled] = useState<boolean>(false);
  const [countriesOptions, setCountriesOptions] = useState<
    {
      label: string;
      value: string;
    }[]
  >([]);

  useEffect(() => {
    const countriesNamesAndIsoLocalized = Object.entries(
      countries.getNames(getValidLocale(router.locale ?? 'en').toLowerCase(), {
        select: 'official',
      })
    ).map(([countryIso, countryName]) => ({
      label: countryName,
      value: countryIso,
    }));
    const countriesOptions = countriesWithFlagsAndPrefix.map(country => country.cc.toUpperCase());
    const finalCountries = countriesNamesAndIsoLocalized.filter(country =>
      countriesOptions.includes(country.value.toUpperCase())
    );

    setCountriesOptions(finalCountries);
  }, [countries, router.locale]);

  return (
    <form onSubmit={submitUser} className="tw-flex tw-flex-col tw-gap-12">
      <div className="tw-h-full">
        <h2 className="tw-font-plusJakartaSans tw-text-2xl tw-font-bold tw-leading-[30.24px] tw-text-text max-nine:!tw-text-lg">
          {translate(OnboardingEnum['personal_information.personal_data'])}
        </h2>
        <div className="tw-pt-5">
          <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-4 child:tw-min-w-80 child:tw-grow child:tw-basis-[calc(50%-1rem)]">
            <div className="tw-flex tw-flex-col tw-gap-3">
              <SpiritoryLabel htmlFor="firstName" required>
                {translate(OnboardingEnum['personal_information.first_name'])}
              </SpiritoryLabel>
              <SpiritoryInput
                type="text"
                name="firstName"
                id="firstName"
                placeholder="Stefan"
                required
                onChange={handleUserChange}
                value={user.firstName}
              />
            </div>
            <div className="tw-flex tw-flex-col tw-gap-3">
              <SpiritoryLabel htmlFor="lastName" required>
                {translate(OnboardingEnum['personal_information.last_name'])}
              </SpiritoryLabel>
              <SpiritoryInput
                type="text"
                name="lastName"
                id="lastName"
                placeholder="Hoffmann"
                required
                onChange={handleUserChange}
                value={user.lastName}
              />
            </div>
            <div className="tw-flex tw-flex-col tw-gap-3">
              <SpiritoryLabel htmlFor="email" required>
                {translate(OnboardingEnum['personal_information.email'])}
              </SpiritoryLabel>
              <SpiritoryInput
                type="text"
                name="email"
                id="email"
                defaultValue={user.email}
                readOnly
                disabled
              />
            </div>
            <div>
              <PhoneDropdown
                phoneNumber={user.displayPhoneNumber || user.phoneNumber || ''}
                handlePhoneChange={handleUserChange}
                currentPhonePrefixUser={currentPhonePrefix}
                setCurrentPhonePrefixUser={setCurrentPhonePrefix}
                loading={loading}
                isOnBoarding={true}
                required={true}
              />
            </div>
          </div>
          <hr className="tw-my-8 tw-border-t-2 tw-border-platinum-gray" />
          <div className="tw-mb-8 tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-4 child:tw-min-w-80 child:tw-grow child:tw-basis-[calc(50%-1rem)]">
            <div className="tw-flex tw-flex-col tw-gap-3">
              <SpiritoryLabel htmlFor="dob" required>
                {translate(OnboardingEnum['personal_information.date_of_birth'])}
              </SpiritoryLabel>
              <SpiritoryInput
                type="date"
                name="dob"
                id="dob"
                required
                onChange={handleDobChange}
                value={user.dob ? user.dob.split('T')[0] : ''}
              />
            </div>
            <div className="tw-flex tw-flex-col tw-gap-3">
              <SpiritoryLabel htmlFor="dob" required>
                {translate(OnboardingEnum['personal_information.language'])}
              </SpiritoryLabel>
              <SpiritorySelect
                name="local"
                defaultOptionText={translateCommon(CommonEnum.select_language)}
                value={user.local || ''}
                onChange={handleUserChange}
                options={[
                  {
                    label: `${translate(OnboardingEnum['personal_information.languages.english'])} (English)`,
                    value: 'EN',
                  },
                  {
                    label: `${translate(OnboardingEnum['personal_information.languages.spanish'])} (Español)`,
                    value: 'ES',
                  },
                  {
                    label: `${translate(OnboardingEnum['personal_information.languages.french'])} (Français)`,
                    value: 'FR',
                  },
                  {
                    label: `${translate(OnboardingEnum['personal_information.languages.italian'])} (Italiano)`,
                    value: 'IT',
                  },
                  {
                    label: `${translate(OnboardingEnum['personal_information.languages.german'])} (Deutsch)`,
                    value: 'DE',
                  },
                ]}
              />
            </div>
          </div>
          <h2 className="tw-mb-8 tw-font-plusJakartaSans tw-text-lg tw-font-bold tw-leading-[30.24px] tw-text-text">
            {translate(OnboardingEnum['personal_information.address'])}
          </h2>
          <div className="tw-pt-5">
            <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-4 child:tw-min-w-80 child:tw-grow child:tw-basis-[calc(50%-1rem)]">
              <div className="tw-flex tw-flex-col tw-gap-3">
                <SpiritoryLabel htmlFor="dob" required>
                  {translate(OnboardingEnum['personal_information.country'])}
                </SpiritoryLabel>
                <SpiritorySelect
                  name="countryCode"
                  defaultOptionText={translate(OnboardingEnum['seller.business.select_a_country'])}
                  value={user.userAddress?.countryCode || ''}
                  onChange={handleUserAddressChange}
                  options={countriesOptions}
                />
              </div>
              <div className="tw-flex tw-flex-col tw-gap-3">
                <SpiritoryLabel htmlFor="dob" required>
                  {translate(OnboardingEnum['personal_information.city'])}
                </SpiritoryLabel>
                <SpiritoryInput
                  type="text"
                  name="city"
                  id="city"
                  placeholder={translateCommon(CommonEnum.city)}
                  required
                  onChange={handleUserAddressChange}
                  value={user.userAddress?.city}
                />
              </div>
              <div className="tw-flex tw-flex-col tw-gap-3">
                <SpiritoryLabel htmlFor="line1" required>
                  {translate(OnboardingEnum['personal_information.address'])}
                </SpiritoryLabel>
                <SpiritoryInput
                  type="text"
                  name="line1"
                  id="line1"
                  placeholder={translateCommon(CommonEnum.address)}
                  required
                  onChange={handleUserAddressChange}
                  value={user.userAddress?.line1}
                />
              </div>
              <div className="tw-flex tw-flex-col tw-gap-3">
                <SpiritoryLabel htmlFor="postalCode" required>
                  {translate(OnboardingEnum['personal_information.zip_code'])}
                </SpiritoryLabel>
                <SpiritoryInput
                  type="text"
                  name="postalCode"
                  id="postalCode"
                  placeholder={translateCommon(CommonEnum.postal_code)}
                  required
                  onChange={handleUserAddressChange}
                  value={user.userAddress?.postalCode}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="tw-flex tw-w-full tw-items-center tw-justify-between child:tw-basis-1/3 max-nine:tw-flex-col max-nine:tw-items-stretch max-nine:tw-gap-4 max-nine:child:tw-basis-full">
        <PrimaryButton outlined={true} onClick={() => router.back()}>
          {translate(OnboardingEnum['common.back'])}
        </PrimaryButton>

        <PrimaryButton type="submit" disabled={loadingSubmitUser || submitDisabled}>
          {translate(OnboardingEnum['common.next'])}
        </PrimaryButton>
      </div>
    </form>
  );
}
